import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { useAuth } from '../../context/AuthContext';
import EpisodeManager from '../../components/EpisodeManager';
import ServiceManager from '../../components/ServiceManager';
import BookingInbox from '../../components/BookingInbox';
import { API } from '../../config/api';
import {
  Mic2, Users, Wrench, Eye,
  Loader2, ExternalLink, Inbox, Headphones
} from 'lucide-react';

export default function PodcasterDashboard() {
  const { user, isAuthenticated, loading: authLoading, getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ followers: 0, views: 0, bookings: 0, episodes: 0 });
  const [activeTab, setActiveTab] = useState('episodes');

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) { navigate('/login'); return; }
    if (user?.role && user.role !== 'podcaster' && user.role !== 'owner') {
      navigate(`/${user.role}/dashboard`); return;
    }
    fetchStats();
  }, [isAuthenticated, authLoading, user]);

  const fetchStats = async () => {
    try {
      const headers = getAuthHeaders();
      const [followRes, bookRes, epRes] = await Promise.all([
        fetch(`${API}/users/${user?.user_id}/followers?limit=1`, { headers }),
        fetch(`${API}/bookings/me`, { headers }),
        fetch(`${API}/creative-episodes/me`, { headers }),
      ]);
      const fd = followRes.ok ? await followRes.json() : {};
      const bd = bookRes.ok ? await bookRes.json() : {};
      const ed = epRes.ok ? await epRes.json() : {};
      setStats({
        followers: fd.total || fd.followers?.length || 0,
        views: 0,
        bookings: bd.bookings?.length || 0,
        episodes: ed.episodes?.length || 0,
      });
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  if (authLoading || loading) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-muted-foreground" /></div>;
  }

  return (
    <div className="min-h-screen py-8 px-4" data-testid="podcaster-dashboard">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-heading text-2xl font-bold flex items-center gap-2">
              <Mic2 className="w-6 h-6 text-green-500" />Podcaster Dashboard
            </h1>
            <p className="text-sm text-muted-foreground mt-1">Manage your episodes, services, and bookings</p>
          </div>
          <Button variant="outline" onClick={() => navigate(`/u/${user?.handle}`)} data-testid="view-profile-link">
            <ExternalLink className="w-4 h-4 mr-2" />View Profile
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
          {[
            { label: 'Followers', value: stats.followers, icon: Users, color: 'text-blue-400' },
            { label: 'Profile Views', value: stats.views, icon: Eye, color: 'text-green-400' },
            { label: 'Bookings', value: stats.bookings, icon: Inbox, color: 'text-emerald-400' },
            { label: 'Episodes', value: stats.episodes, icon: Headphones, color: 'text-green-400' },
          ].map(s => (
            <Card key={s.label} className="glass-card border-border p-4">
              <s.icon className={`w-5 h-5 ${s.color} mb-2`} />
              <p className="text-2xl font-bold">{s.value}</p>
              <p className="text-xs text-muted-foreground">{s.label}</p>
            </Card>
          ))}
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="w-full justify-start bg-muted/30 p-1 mb-6">
            <TabsTrigger value="episodes" className="flex items-center gap-2" data-testid="dash-tab-episodes">
              <Mic2 className="w-4 h-4" />Episodes
            </TabsTrigger>
            <TabsTrigger value="services" className="flex items-center gap-2" data-testid="dash-tab-services">
              <Wrench className="w-4 h-4" />Services
            </TabsTrigger>
            <TabsTrigger value="bookings" className="flex items-center gap-2" data-testid="dash-tab-bookings">
              <Inbox className="w-4 h-4" />Bookings
              {stats.bookings > 0 && <Badge variant="secondary" className="ml-1 text-xs">{stats.bookings}</Badge>}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="episodes"><EpisodeManager /></TabsContent>
          <TabsContent value="services"><ServiceManager /></TabsContent>
          <TabsContent value="bookings"><BookingInbox /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
